/**
 * What the jobs list prints about a job (DESIGN UD-21 jobs list): the document's name (or "Unnamed document" and the first 8
 * characters of its hash for a job that predates the name), the local date and time it started, the setup in words, the
 * progress. Pure functions of the record, so they are tested in Node; the date is in the viewer's local time.
 */
import { fmt2, fmtInt } from '../format.ts';
import { COST, JOBS } from '../strings-bench.ts';
import { actionPhrase, jobAction, withGuardrailsLabel } from './action-label.ts';
import type { BenchJob } from './job.ts';

/** The name to show, and the hash tail that stands in for a missing name (null when the job has a name). */
export function jobName(job: Pick<BenchJob, 'docName' | 'docHash'>): {
	name: string;
	hash: string | null;
} {
	const name = job.docName?.trim();
	if (name) return { name, hash: null };
	return { name: JOBS.unnamed, hash: job.docHash.slice(0, 8) };
}

/** The name as one string, with the hash after it for an unnamed job ("Unnamed document a1b2c3d4"). */
export function jobTitle(job: Pick<BenchJob, 'docName' | 'docHash'>): string {
	const { name, hash } = jobName(job);
	return hash ? `${name} ${hash}` : name;
}

const parts = (iso: string, withYear: boolean): string | null => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return null;
	const f = new Intl.DateTimeFormat('en-GB', {
		day: 'numeric',
		month: 'short',
		...(withYear ? { year: 'numeric' as const } : {}),
		hour: '2-digit',
		minute: '2-digit',
		hourCycle: 'h23',
	});
	const p = Object.fromEntries(f.formatToParts(d).map((x) => [x.type, x.value]));
	const date = `${p.day} ${p.month}${withYear ? ` ${p.year}` : ''}`;
	return `${date}${withYear ? ',' : ''} ${p.hour}:${p.minute}`;
};

/** "16 Oct 2026, 19:52": the date and time a job started, in local time. */
export const fmtJobTime = (iso: string): string => parts(iso, true) ?? iso;

/** "16 Oct 19:52": the same without the year, for the header menu. */
export const fmtJobTimeShort = (iso: string): string => parts(iso, false) ?? iso;

/** The job's model and effort in words ("Sonnet 5.5, high effort"); empty when the record names no model. */
export function jobModelEffort(job: Pick<BenchJob, 'model' | 'effort'>): string {
	const model =
		job.model === ''
			? ''
			: job.model.includes('haiku')
				? COST.modelNames.haiku
				: job.model.includes('sonnet')
					? COST.modelNames.sonnet.replace(' (recommended)', '')
					: job.model;
	const effort =
		job.effort === '' ? '' : (COST.efforts[job.effort as keyof typeof COST.efforts] ?? job.effort);
	return model === '' ? '' : JOBS.setup(model, effort === '' ? '' : effort.toLowerCase());
}

/** The job's route, model and effort in words ("Standard · Sonnet 5.5, high effort"). */
export function jobSetup(
	job: Pick<BenchJob, 'economy' | 'model' | 'effort' | 'guardrails'>
): string {
	const route = job.economy ? JOBS.routes.economy : JOBS.routes.standard;
	const me = jobModelEffort(job);
	const setup = me === '' ? route : `${route} · ${me}`;
	// two runs of one document are told apart by the editorial guardrails (ED-191): named only when on
	return job.guardrails ? `${setup} · ${JOBS.guardrailsOn}` : setup;
}

/**
 * The opening words of the job's text, for a job whose name does not tell it apart from another (pasted text, the sample, a
 * job from before names were kept): its working text, else its rewritten text. Null for a named file or web page, and for a
 * record that holds neither text. A word cut by the read limit is dropped; "…" marks that more follows.
 */
export function jobExcerpt(
	job: Pick<BenchJob, 'docName' | 'sourceKind' | 'baseText' | 'resultText'>,
	words = 12
): string | null {
	const generic = !job.docName?.trim() || job.sourceKind === 'paste' || job.sourceKind === 'sample';
	const text = generic ? (job.baseText ?? job.resultText) : undefined;
	if (!text) return null;
	const slice = text.slice(0, 80 * words);
	const tokens = slice.split(/\s+/).filter(Boolean);
	if (text.length > slice.length) tokens.pop();
	if (tokens.length === 0) return null;
	const more = tokens.length > words || text.length > slice.length;
	return `${tokens.slice(0, words).join(' ')}${more ? '…' : ''}`;
}

/**
 * The passages the guardrails' editor left as they are (GR2, ED-194), for a finished job that ran with the guardrails; zero
 * for every other job (so nothing is shown without the option).
 */
export const jobDeclined = (job: Pick<BenchJob, 'guardrails' | 'report'>): number =>
	job.guardrails ? (job.report?.declined?.length ?? 0) : 0;

/** Stretches done of total, as text. */
export const jobProgress = (job: Pick<BenchJob, 'doneChunks' | 'totalChunks'>): string =>
	JOBS.stretches(fmtInt(job.doneChunks), fmtInt(job.totalChunks));

/** Done over total, 0 to 1. */
export const jobFraction = (job: Pick<BenchJob, 'doneChunks' | 'totalChunks'>): number =>
	job.totalChunks > 0 ? Math.max(0, Math.min(1, job.doneChunks / job.totalChunks)) : 0;

/** The spend so far, or null for a job that recorded none. */
export const jobCost = (job: Pick<BenchJob, 'costUsd'>): string | null =>
	job.costUsd > 0 ? JOBS.costSoFar(fmt2(job.costUsd)) : null;

/**
 * The header menu's text for a job: "The Eleventh p5 · rewrite the flagged sections · 16 Oct 19:52 · done" (UD-29); a job with a generic name carries its first
 * words after it ("Pasted text “It was the eleventh…” · 16 Oct 20:01 · running").
 */
export function jobMenuLabel(
	job: Pick<
		BenchJob,
		| 'docName'
		| 'docHash'
		| 'createdAt'
		| 'status'
		| 'sourceKind'
		| 'baseText'
		| 'resultText'
		| 'scope'
		| 'guardrails'
	>
): string {
	const ex = jobExcerpt(job, 5);
	const name = ex ? `${jobTitle(job)} ${JOBS.excerpt(ex)}` : jobTitle(job);
	const step = jobAction(job);
	return JOBS.menuItem(
		name,
		fmtJobTimeShort(job.createdAt),
		JOBS[job.status],
		step ? withGuardrailsLabel(actionPhrase(step), job.guardrails) : ''
	);
}

/** "00:41" for a time today, else the date and time without the year ("16 Oct 00:41"). */
export function fmtStopTime(iso: string, now: Date = new Date()): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	const sameDay =
		d.getFullYear() === now.getFullYear() &&
		d.getMonth() === now.getMonth() &&
		d.getDate() === now.getDate();
	if (!sameDay) return fmtJobTimeShort(iso);
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Why a job stopped, in a sentence (UD-38), or null for a job that has not: a paused job says what ended its run and how to
 * go on, an errored one points to its details. A paused job without a recorded reason says that the page running it stopped.
 */
export function stopText(
	job: Pick<BenchJob, 'status' | 'stop' | 'doneChunks' | 'totalChunks' | 'economy'>,
	now: Date = new Date()
): string | null {
	const at = (iso: string | undefined): string => (iso ? fmtStopTime(iso, now) : '');
	const tail = JOBS.stop.resume(fmtInt(job.doneChunks), fmtInt(job.totalChunks));
	const stop = job.stop;
	if (job.status === 'error') return stop?.at ? JOBS.stop.error(at(stop.at)) : null;
	if (job.status !== 'paused') return null;
	switch (stop?.reason) {
		case 'closed':
			return JOBS.stop.closed(at(stop.at), tail);
		case 'hidden':
			return JOBS.stop.hidden(at(stop.at), tail);
		case 'offline':
			return JOBS.stop.offline(at(stop.at), tail);
		case 'budget':
			return JOBS.stop.budget(at(stop.at), tail);
		case 'error':
			return JOBS.stop.error(at(stop.at));
		default:
			return JOBS.stop.lost(stop?.lastSeenAt ? at(stop.lastSeenAt) : null, tail);
	}
}
